/**
 * ShadowCollector — portable shadow caster collection from Level.
 *
 * Iterates all GameObjects in the given Level, reads BoxCollider2D,
 * CircleCollider2D, CapsuleCollider2D, PolygonCollider2D, and ConvexCollider2D
 * components that have `castShadow` enabled, converts their shapes to ShadowCaster2D
 * descriptors for the ShadowMapPass.
 *
 * Zero editor dependencies — works identically in editor and player.
 *
 * @module @engine/core
 */

import type { Level } from './Level';
import type { ShadowCaster2D } from '@engine/render2d';

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Collect all shadow casters from collider components in the level.
 *
 * Reads BoxCollider2D, CircleCollider2D, CapsuleCollider2D, PolygonCollider2D,
 * ConvexCollider2D where `castShadow === true`. Applies the owning GameObject's
 * transform (position, rotation, scale) to produce world-space `ShadowCaster2D[]`.
 *
 * @param level - The Level to iterate
 * @returns Array of shadow caster descriptors for the ShadowMapPass
 */
export function collectShadowCastersFromLevel(level: Level): ShadowCaster2D[] {
  const casters: ShadowCaster2D[] = [];

  for (const go of level.getAllObjects()) {
    const t = go.transform;
    const cx = t.position.x;
    const cy = t.position.y;
    // Negate rotation to match the renderer's Y-flip convention: sprites draw with -rotation
    // (SpriteBatch/GameViewRenderer) and collider gizmos with -rotationRadians, so the shadow occluder must
    // use -θ too. The occluder edge formula (extractBoxEdges) is the SAME CCW formula as the sprite quad, so
    // feeding +θ here mirrors the shadow vs the sprite (sprite "/" → shadow "\").
    const rot = -t.rotationRadians;
    const sx = t.lossyScale.x;
    const sy = t.lossyScale.y;

    // ── Box Collider ──
    const boxData = go.getComponentData('core.BoxCollider2D') as
      | { castShadow?: boolean; offset?: { x: number; y: number }; size?: { x: number; y: number } }
      | undefined;
    if (boxData?.castShadow) {
      const ox = (boxData.offset?.x ?? 0) * sx;
      const oy = (boxData.offset?.y ?? 0) * sy;
      const hw = (boxData.size?.x ?? 1) * 0.5 * Math.abs(sx);
      const hh = (boxData.size?.y ?? 1) * 0.5 * Math.abs(sy);
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const wcx = cx + ox * cosR - oy * sinR;
      const wcy = cy + ox * sinR + oy * cosR;
      casters.push({
        enabled: true,
        shape: { type: 'box', center: [wcx, wcy], halfExtents: [hw, hh], rotation: rot },
      });
    }

    // ── Circle Collider ──
    const circData = go.getComponentData('core.CircleCollider2D') as
      | { castShadow?: boolean; offset?: { x: number; y: number }; radius?: number }
      | undefined;
    if (circData?.castShadow) {
      const ox = (circData.offset?.x ?? 0) * sx;
      const oy = (circData.offset?.y ?? 0) * sy;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const wcx = cx + ox * cosR - oy * sinR;
      const wcy = cy + ox * sinR + oy * cosR;
      const r = (circData.radius ?? 0.5) * Math.max(Math.abs(sx), Math.abs(sy));
      casters.push({
        enabled: true,
        shape: { type: 'circle', center: [wcx, wcy], radius: r, segments: 16 },
      });
    }

    // ── Capsule Collider → approximate as box ──
    const capData = go.getComponentData('core.CapsuleCollider2D') as
      | { castShadow?: boolean; offset?: { x: number; y: number }; size?: { x: number; y: number }; direction?: number }
      | undefined;
    if (capData?.castShadow) {
      const ox = (capData.offset?.x ?? 0) * sx;
      const oy = (capData.offset?.y ?? 0) * sy;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const wcx = cx + ox * cosR - oy * sinR;
      const wcy = cy + ox * sinR + oy * cosR;
      const hw = (capData.size?.x ?? 0.5) * 0.5 * Math.abs(sx);
      const hh = (capData.size?.y ?? 1) * 0.5 * Math.abs(sy);
      casters.push({
        enabled: true,
        shape: { type: 'box', center: [wcx, wcy], halfExtents: [hw, hh], rotation: rot },
      });
    }

    // ── Polygon Collider ──
    const polyData = go.getComponentData('core.PolygonCollider2D') as
      | { castShadow?: boolean; offset?: { x: number; y: number }; points?: Array<{ x: number; y: number }> }
      | undefined;
    if (polyData?.castShadow && polyData.points && polyData.points.length >= 3) {
      const ox = (polyData.offset?.x ?? 0) * sx;
      const oy = (polyData.offset?.y ?? 0) * sy;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const flatVerts: number[] = [];
      for (const v of polyData.points) {
        const lx = v.x * sx + ox;
        const ly = v.y * sy + oy;
        flatVerts.push(cx + lx * cosR - ly * sinR, cy + lx * sinR + ly * cosR);
      }
      casters.push({
        enabled: true,
        shape: { type: 'polygon', vertices: flatVerts },
      });
    }

    // ── Convex Collider (sprite-based) ──
    const convexData = go.getComponentData('core.ConvexCollider2D') as
      | { castShadow?: boolean; offset?: { x: number; y: number }; points?: Array<{ x: number; y: number }> }
      | undefined;
    if (convexData?.castShadow && convexData.points && convexData.points.length >= 3) {
      const ox = (convexData.offset?.x ?? 0) * sx;
      const oy = (convexData.offset?.y ?? 0) * sy;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const flatVerts: number[] = [];
      for (const v of convexData.points) {
        const lx = v.x * sx + ox;
        const ly = v.y * sy + oy;
        flatVerts.push(cx + lx * cosR - ly * sinR, cy + lx * sinR + ly * cosR);
      }
      casters.push({
        enabled: true,
        shape: { type: 'polygon', vertices: flatVerts },
      });
    }
  }

  return casters;
}
